// WP-1.20 (S-45 Backups, BK-2): backup status (last run / failure, from the settings the ops
// cron writes) and the data-ownership "Export everything" download. Restore is a documented
// drill (RB-2/4), not a one-click action — the copy points at the runbook.
import { Download } from 'lucide-react';
import { useSettings } from '../../lib/settings';
import { EXPORT_EVERYTHING_URL } from '../../lib/system';

function asDate(v: unknown): string | undefined {
  return typeof v === 'string' ? v.slice(0, 16).replace('T', ' ') : undefined;
}

export function SettingsBackups(): React.JSX.Element {
  const { data: settings } = useSettings();
  const lastBackupAt = asDate(settings?.lastBackupAt);
  const lastFailedAt = asDate(settings?.lastBackupFailedAt);

  return (
    <div className="space-y-6">
      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-3 text-sm font-medium text-ink">Status</h2>
        <p className="text-sm text-ink">
          Last successful backup:{' '}
          <span className="text-muted">{lastBackupAt ?? 'never recorded'}</span>
        </p>
        {lastFailedAt !== undefined && (
          <p className="mt-1 text-sm text-danger">Last failure reported: {lastFailedAt}</p>
        )}
        <p className="mt-2 text-xs text-muted">
          Nightly encrypted backups run on the server (pg_dump → age → OneDrive). Failures raise a
          banner and email (OD-7).
        </p>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Export everything</h2>
        <p className="mb-3 text-xs text-muted">
          Download your entire dataset in open formats (event log JSON, transactions CSV, settings,
          and every attachment) — your data is never hostage to this app (BK-2, SC-6).
        </p>
        <a
          href={EXPORT_EVERYTHING_URL}
          download="pfos-export.zip"
          className="inline-flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-4 py-2 text-sm font-medium text-primary-ink hover:opacity-90"
        >
          <Download size={16} /> Export everything
        </a>
      </section>

      <section className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h2 className="mb-1 text-sm font-medium text-ink">Restore</h2>
        <p className="text-xs text-muted">
          Restores are a deliberate, verified drill: recreate the stack, restore the latest
          encrypted <code>pg_dump</code>, and run reconciliation to confirm zero drift (see
          docs/runbooks, RB-2/4).
        </p>
      </section>
    </div>
  );
}
